<template>
  <div class="batch-barcode-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>批量生成条形码</el-breadcrumb-item>
      </el-breadcrumb>
      <h1>批量生成条形码</h1>
      <p>支持Excel上传或手动输入数据，批量生成条形码并导出为PDF</p>
    </div>

    <!-- 数据输入与配置 -->
    <div class="main-config-section">
      <el-card shadow="hover">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-s-data"></i> 数据配置 ({{ tableData.length }} 条记录)</span>
          <div class="header-buttons">
            <el-button size="small" type="success" icon="el-icon-download" @click="downloadTemplate">
              下载模板
            </el-button>
            <el-button size="small" type="danger" icon="el-icon-delete" @click="clearData" v-if="tableData.length > 0">
              清空数据
            </el-button>
          </div>
        </div>

        <!-- 左右分栏布局 -->
        <el-row :gutter="24">
          <!-- 左侧：数据列表 -->
          <el-col :span="14">
            <div class="data-section">
              <div class="section-header">
                <span class="section-title">
                  <i class="el-icon-edit-outline"></i>
                  数据列表 (点击单元格可直接编辑)
                </span>
                <div class="data-actions">
                  <el-upload class="upload-button" action="#" :auto-upload="false" :on-change="handleExcelUpload"
                    accept=".xlsx,.xls" :show-file-list="false">
                    <el-button type="primary" icon="el-icon-upload2" size="small">
                      上传Excel
                    </el-button>
                  </el-upload>
                  <el-button type="success" icon="el-icon-plus" size="small" @click="addDataRow">
                    添加行
                  </el-button>
                  <el-button type="warning" icon="el-icon-data-board" size="small" @click="loadTestData">
                    载入测试数据
                  </el-button>
                </div>
              </div>

              <template v-if="tableData.length <= 800">
                <el-table :data="tableData" border stripe size="small" style="width: 100%" max-height="600"
                  :empty-text="tableData.length === 0 ? '暂无数据，请上传Excel文件或手动添加数据' : ''" class="data-table">
                  <el-table-column type="index" label="序号" width="60" align="center" fixed="left" />
                  <el-table-column v-for="(field, index) in dataFields" :key="field" :prop="field" :label="field"
                    :min-width="index === 0 ? '200' : '150'" show-overflow-tooltip>
                    <template slot-scope="scope">
                      <el-input v-model="scope.row[field]" size="mini" placeholder="点击编辑" @change="updateTableData"
                        style="border: none; background: transparent;" />
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="100" align="center" fixed="right">
                    <template slot-scope="scope">
                      <el-button type="text" size="mini" icon="el-icon-delete" @click="removeRow(scope.$index)" 
                                 style="color: #f56c6c; padding: 5px;">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </template>
              <template v-else>
                <div style="display:flex; justify-content:space-between; align-items:center; margin:6px 0 4px;">
                  <span style="font-size:12px; color:#909399;">已载入 {{ tableData.length }} 行（虚拟滚动）</span>
                  <el-button size="mini" type="primary" @click="scrollTopVirtual">回到顶部</el-button>
                </div>
                <VirtualTable
                  :rows="tableData"
                  :columns="dataFields"
                  :keeps="40"
                  :row-height="36"
                  :height="480"
                  @cell-change="onVirtualCellChange"
                  @remove-row="removeRow"
                />
              </template>
            </div>
          </el-col>

          <!-- 右侧：生成配置 -->
          <el-col :span="10">
            <div class="config-section">
              <div class="section-header">
                <span class="section-title">
                  <i class="el-icon-setting"></i>
                  生成配置
                </span>
                <el-button type="primary" size="small" icon="el-icon-magic-stick" @click="startGenerate" 
                           :loading="generating || generatingZip" :disabled="barcodeLabels.length === 0">
                  {{ (generating || generatingZip) ? '制作中...' : '开始制作' }}
                </el-button>
              </div>

              <div class="generation-config">
                <el-form :model="config" label-position="top" size="small">

                  <!-- 页面布局设置（置顶） -->
                  <div class="config-group">
                    <h4 class="group-title">页面布局</h4>
                    
                    <!-- 页面类型选择 -->
                    <el-row :gutter="12">
                      <el-col :span="24">
                        <el-form-item label="生成类型">
                          <el-radio-group v-model="config.generateType" @change="onGenerateTypeChange">
                            <el-radio label="multiA4">多标签A4布局</el-radio>
                            <el-radio label="singlePDF">单页标签（PDF）</el-radio>
                            <el-radio label="singleImage">单页标签（图片）</el-radio>
                          </el-radio-group>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- A4多标签行列分布 -->
                    <el-row :gutter="12" v-if="config.generateType === 'multiA4'">
                      <el-col :span="16">
                        <el-form-item label="行列分布">
                          <el-row :gutter="8">
                            <el-col :span="10">
                              <el-input-number v-model="config.rows" :min="1" :max="10" controls-position="right"
                                style="width: 100%;" @change="onLayoutChange">
                              </el-input-number>
                            </el-col>
                            <el-col :span="4" style="text-align: center; line-height: 28px; color: #909399;">×</el-col>
                            <el-col :span="10">
                              <el-input-number v-model="config.cols" :min="1" :max="10" controls-position="right"
                                style="width: 100%;" @change="onLayoutChange">
                              </el-input-number>
                            </el-col>
                          </el-row>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- 单页标签尺寸配置 -->
                    <el-row :gutter="12" v-if="config.generateType === 'singlePDF' || config.generateType === 'singleImage'">
                      <el-col :span="12">
                        <el-form-item label="标签宽度(mm)">
                          <el-input-number v-model="config.singlePageWidth" :min="50" :max="200" controls-position="right"
                            style="width: 100%;" @change="onLayoutChange">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="标签高度(mm)">
                          <el-input-number v-model="config.singlePageHeight" :min="20" :max="150" controls-position="right"
                            style="width: 100%;" @change="onLayoutChange">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>

                  <!-- 内容配置 -->
                  <div class="config-group">
                    <h4 class="group-title">内容配置</h4>
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item label="条形码字段">
                          <el-select v-model="config.barcodeField" placeholder="请选择" style="width: 100%;" filterable>
                            <el-option v-for="field in dataFields" :key="field" :label="field" :value="field">
                            </el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="条形码类型">
                          <el-select v-model="config.barcodeType" style="width: 100%;">
                            <el-option label="CODE128" value="CODE128"></el-option>
                            <el-option label="CODE39" value="CODE39"></el-option>
                            <el-option label="EAN13" value="EAN13"></el-option>
                            <el-option label="EAN8" value="EAN8"></el-option>
                            <el-option label="UPC" value="UPC"></el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 条形码尺寸配置 -->
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item label="条形码宽度(mm)">
                          <el-input-number v-model="config.barcodeWidth" :min="30" :max="80" controls-position="right"
                            style="width: 100%;">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="条形码高度(mm)">
                          <el-input-number v-model="config.barcodeHeight" :min="10" :max="30" controls-position="right"
                            style="width: 100%;">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 文字配置 -->
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item>
                          <span slot="label">
                            显示文字
                            <el-switch v-model="config.showText" size="mini"></el-switch>
                          </span>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12" v-if="config.showText">
                        <el-form-item label="文字说明">
                          <span style="color: #909399; font-size: 12px;">自动显示条形码内容</span>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 文字样式配置 -->
                    <div v-if="config.showText">
                      <el-row :gutter="12">
                        <el-col :span="12">
                          <el-form-item label="字体大小(mm)">
                            <el-input-number v-model="config.fontSize" :min="2" :max="8" :step="0.5"
                              controls-position="right" style="width: 100%;">
                            </el-input-number>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item label="文字间距(mm)">
                            <el-input-number v-model="config.textSpacing" :min="0" :max="10" :step="0.5"
                              controls-position="right" style="width: 100%;">
                            </el-input-number>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </div>
                  </div>

                  <!-- 样式配置 -->
                  <div class="config-group">
                    <h4 class="group-title">样式配置</h4>
                    <el-row :gutter="12">
                      <el-col :span="8">
                        <el-form-item>
                          <span slot="label">
                            显示边框
                            <el-switch v-model="config.showBorder" size="mini"></el-switch>
                          </span>
                        </el-form-item>
                      </el-col>
                      <el-col :span="8" v-if="config.showBorder">
                        <el-form-item label="边框颜色">
                          <el-color-picker v-model="config.borderColor" size="small"></el-color-picker>
                        </el-form-item>
                      </el-col>
                      <el-col :span="8">
                        <el-form-item>
                          <span slot="label">
                            显示背景
                            <el-switch v-model="config.showBackground" size="mini"></el-switch>
                          </span>
                          <el-color-picker v-if="config.showBackground" v-model="config.backgroundColor" size="small" 
                                          style="margin-left: 10px;"></el-color-picker>
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>
                </el-form>
              </div>
            </div>
          </el-col>
        </el-row>
      </el-card>
    </div>

    <!-- 预览和生成 -->
    <div class="preview-section">
      <!-- 无数据提示 -->
      <el-card shadow="hover" v-if="barcodeLabels.length === 0">
        <div style="text-align: center; padding: 40px;">
          <i class="el-icon-info" style="font-size: 48px; color: #909399; margin-bottom: 16px;"></i>
          <h3 style="color: #909399; margin-bottom: 16px;">暂无有效数据</h3>
          <p style="color: #C0C4CC; margin-bottom: 20px;">请填写表格数据或上传Excel文件</p>
          <div>
            <el-button type="primary" icon="el-icon-upload" @click="() => document.querySelector('.upload-button input').click()">
              上传Excel
            </el-button>
            <el-button type="success" icon="el-icon-plus" @click="addDataRow">
              添加数据行
            </el-button>
            <el-button type="info" icon="el-icon-data-board" @click="loadTestData">
              载入示例
            </el-button>
          </div>
        </div>
      </el-card>
      
      <!-- 有数据时的预览 -->
      <el-card shadow="hover" v-if="barcodeLabels.length > 0">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-view"></i> 标签预览</span>
          <div class="header-buttons">
            <el-button-group size="small" style="margin-right: 10px;">
              <el-button icon="el-icon-arrow-left" :disabled="currentLabelIndex <= 0" @click="prevLabel">
                上一个
              </el-button>
              <el-button icon="el-icon-arrow-right" :disabled="currentLabelIndex >= barcodeLabels.length - 1" @click="nextLabel">
                下一个
              </el-button>
            </el-button-group>
          </div>
        </div>

        <!-- 预览区域 - 单个标签预览 -->
        <div class="preview-container">
          <div class="single-label-preview" v-if="currentPreviewLabel">
            <div class="barcode-label-item" 
                 :style="getSingleLabelStyle()"
                 :class="getLabelClass()">
              
              <!-- 条形码和文字容器 -->
              <div class="barcode-content" :style="getContentStyle()">
                <!-- 条形码 -->
                <div class="barcode-container">
                  <svg :ref="`previewBarcode`" 
                       :width="config.barcodeWidth + 'mm'" 
                       :height="(config.barcodeHeight + (config.showText ? 8 : 0)) + 'mm'">
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- 标签信息 -->
            <div class="label-info">
              <p><strong>标签 {{ currentLabelIndex + 1 }}/{{ barcodeLabels.length }}</strong></p>
              <p>条形码内容: {{ currentPreviewLabel.barcodeContent }}</p>
              <p>条形码类型: {{ config.barcodeType }}</p>
              <p v-if="config.showText">显示条形码下方文字: 是</p>
            </div>
          </div>
        </div>

        <div class="page-info">
          共 {{ barcodeLabels.length }} 个标签
          <span v-if="config.generateType === 'singleImage'" style="margin-left: 10px; color: #409EFF;">
            (单页图片: {{ config.singlePageWidth }}×{{ config.singlePageHeight }}mm)
          </span>
          <span v-else-if="config.generateType === 'singlePDF'" style="margin-left: 10px; color: #E6A23C;">
            (单页PDF: {{ config.singlePageWidth }}×{{ config.singlePageHeight }}mm)
          </span>
          <span v-else style="margin-left: 10px; color: #67C23A;">
            (A4纸 {{ config.rows }}×{{ config.cols }} 布局)
          </span>
        </div>
      </el-card>
    </div>

    <!-- 隐藏的标签容器 - 用于PDF生成 -->
  <div v-if="showHiddenForZip" class="hidden-labels-container" style="position: absolute; left: -9999px; top: -9999px;">
      <div v-for="(label, labelIndex) in barcodeLabels" 
           :key="`hidden-label-${labelIndex}`"
           :ref="`hiddenLabel_${labelIndex}`"
           class="hidden-barcode-label-item" 
           :style="getHiddenLabelStyle()"
           :class="getLabelClass()">
        
        <!-- 条形码和文字容器 -->
        <div class="barcode-content" :style="getContentStyle()">
          <!-- 条形码 -->
          <div class="barcode-container">
            <svg :ref="`hiddenBarcode_${labelIndex}`" 
                 :width="config.barcodeWidth + 'mm'" 
                 :height="(config.barcodeHeight + (config.showText ? 8 : 0)) + 'mm'">
            </svg>
          </div>
        </div>
      </div>
    </div>
    
    <!-- PDF生成进度弹窗 -->
    <el-dialog
      title="PDF生成进度"
      :visible.sync="showProgress"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align: center; padding: 20px;">
        <div style="margin-bottom: 20px; font-size: 16px; color: #606266;">
          正在生成PDF文件，请稍候...
        </div>
        <el-progress 
          :percentage="generateProgress" 
          :status="generateProgress === 100 ? 'success' : null"
          :stroke-width="10">
        </el-progress>
        <div style="margin-top: 20px; color: #909399; font-size: 14px;">
          请勿关闭页面
        </div>
      </div>
    </el-dialog>

    <!-- 图片ZIP生成进度弹窗 -->
    <el-dialog
      title="图片生成进度"
      :visible.sync="showZipProgress"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align: center; padding: 20px;">
        <div style="margin-bottom: 20px; font-size: 16px; color: #606266;">
          正在生成图片文件，请稍候...
        </div>
        <el-progress 
          :percentage="zipProgress" 
          :status="zipProgress === 100 ? 'success' : null"
          :stroke-width="10">
        </el-progress>
        <div style="margin-top: 20px; color: #909399; font-size: 14px;">
          请勿关闭页面
        </div>
      </div>
    </el-dialog>

    <!-- Excel 导入进度弹窗 -->
    <el-dialog
      title="Excel导入进度"
      :visible.sync="showImportProgress"
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align:center; padding:20px;">
        <div style="margin-bottom:16px; font-size:15px; color:#606266;">正在分批导入Excel数据...</div>
        <el-progress :percentage="importProgress" :stroke-width="10" :status="importProgress===100?'success':null" />
        <div style="margin-top:16px; color:#909399; font-size:12px;">大型文件采用分批追加避免界面卡顿</div>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import JsBarcode from 'jsbarcode'
import html2canvas from 'html2canvas'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import sourceHanFontUrl from '@/assets/SourceHanSansCN-Regular.ttf'
import VirtualTable from '@/components/VirtualTable.vue'

export default {
  name: 'BatchBarcode',
  components: { VirtualTable },
  data() {
    return {
      // 微让步与导入配置常量（可视需要调整）
      YIELD_ITEM_INTERVAL: 40, // 条形码图像缓存阶段：每多少条让出一次主线程
      YIELD_PAGE_INTERVAL: 2,  // 分页绘制阶段：每多少页让出一次主线程
      IMPORT_CHUNK_SIZE: 500,  // Excel增量导入每批条数
      IMPORT_YIELD_INTERVAL: 1, // 导入批次之间是否强制让步（1=每批都让步）
      // 数据相关
      tableData: [],
      dataFields: [],
      
      // 配置相关
      config: {
        // 页面类型配置
        pageType: 'multi', // 'multi': 多标签A4页面, 'single': 单页标签
        // 统一与二维码组件的生成类型，用于单按钮逻辑
        generateType: 'multiA4', // 'multiA4' | 'singlePDF' | 'singleImage'
        
        // A4多标签配置
        rows: 5,
        cols: 2,
        
        // 单页标签尺寸配置
        singlePageWidth: 80, // mm
        singlePageHeight: 40, // mm
        
        // 内容配置
        barcodeField: '',
        barcodeType: 'CODE128',
        showText: true,
        fontSize: 3,
        textSpacing: 2,
        
        // 条形码尺寸
        barcodeWidth: 50,
        barcodeHeight: 15,
        
        showBorder: false,
        borderColor: '#000000',
        showBackground: false,
        backgroundColor: '#ffffff'
      },
      
      // 标签相关
      barcodeLabels: [], // 所有标签数据
      barcodePages: [], // 分页后的标签数据（用于PDF生成）
      currentLabelIndex: 0, // 当前预览的标签索引
      
      // 生成相关
      generating: false,
      generateProgress: 0,
      showProgress: false,
      generatingZip: false,
      zipProgress: 0,
      showZipProgress: false
      ,showHiddenForZip: false
      ,_debounceTimer: null
      ,_suppressGenerate: false // 导入批量追加期间暂停自动重算
      ,importing: false
      ,importProgress: 0
      ,showImportProgress: false
    }
  },
  
  computed: {
    totalPages() {
      return this.barcodePages.length
    },
    
    itemsPerPage() {
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') return 1
      return this.config.rows * this.config.cols
    },
    
    // 当前预览的标签
    currentPreviewLabel() {
      return this.barcodeLabels[this.currentLabelIndex] || null
    }
  },
  
  watch: {
    // 监听配置变化，重新生成标签
    config: {
      handler() {
        this.scheduleGenerateLabels()
      },
      deep: true
    },
    
    // 监听数据变化，重新生成标签
    tableData: {
      handler() {
        this.scheduleGenerateLabels()
      },
      deep: true
    }
  },
  
  mounted() {
    // 初始化测试数据
    this.loadTestData()
  },
  
  methods: {
    // 微让步封装
    microYield() { return new Promise(r => setTimeout(r)) },
    // 点击统一“开始制作”按钮
    startGenerate() {
      // 根据新的 generateType 选择导出方式
      if (this.config.generateType === 'singleImage') {
        this.generateZipImages()
      } else {
        this.generatePDF()
      }
    },
    onGenerateTypeChange() {
      this.generateLabelsAsync()
    },
    onVirtualCellChange({ rowIndex, field, value }) {
      if (this.tableData[rowIndex]) {
        this.$set(this.tableData[rowIndex], field, value)
        this.updateTableData()
      }
    },
    scrollTopVirtual() {
      const vt = this.$el.querySelector('.vt-body')
      if (vt) vt.scrollTop = 0
    },
    // 防抖: 避免每个字段即时修改触发大量计算与 DOM 更新
    scheduleGenerateLabels() {
      if (this._suppressGenerate) return
      if (this._debounceTimer) clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => {
        this.generateLabels()
      }, 250)
    },
    // ========== 数据处理方法 ==========
    
    // 加载测试数据
    loadTestData() {
      this.tableData = [
        { '产品名称': '苹果手机', '产品编码': '1234567890123', '规格': 'iPhone 15' },
        { '产品名称': '华为手机', '产品编码': '2345678901234', '规格': 'Mate 60' },
        { '产品名称': '小米手机', '产品编码': '3456789012345', '规格': '14 Ultra' },
        { '产品名称': '三星手机', '产品编码': '4567890123456', '规格': 'S24 Ultra' },
        { '产品名称': 'OPPO手机', '产品编码': '5678901234567', '规格': 'Find X7' },
        { '产品名称': 'vivo手机', '产品编码': '6789012345678', '规格': 'X100' },
        { '产品名称': '荣耀手机', '产品编码': '7890123456789', '规格': 'Magic6' },
        { '产品名称': '一加手机', '产品编码': '8901234567890', '规格': '12 Pro' }
      ]
      this.updateDataFields()
      this.setDefaultConfig()
    },
    
    // 更新数据字段
    updateDataFields() {
      if (this.tableData.length > 0) {
        this.dataFields = Object.keys(this.tableData[0])
      } else {
        this.dataFields = []
      }
    },
    
    // 设置默认配置
    setDefaultConfig() {
      if (this.dataFields.length > 0) {
        if (!this.config.barcodeField || !this.dataFields.includes(this.config.barcodeField)) {
          this.config.barcodeField = this.dataFields.find(field => field.includes('编码') || field.includes('code')) || this.dataFields[0]
        }
      }
    },
    
    // 添加数据行
    addDataRow() {
      if (this.dataFields.length === 0) {
        this.tableData.push({ '产品名称': '', '产品编码': '', '规格': '' })
        this.updateDataFields()
        this.setDefaultConfig()
      } else {
        const newRow = {}
        this.dataFields.forEach(field => {
          newRow[field] = ''
        })
        this.tableData.push(newRow)
      }
    },
    
    // 删除行
    removeRow(index) {
      this.tableData.splice(index, 1)
    },
    
    // 清空数据
    clearData() {
      this.$confirm('确认清空所有数据吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 保留表头结构，只清空数据
        if (this.dataFields.length > 0) {
          // 创建一个空行保持表头结构
          const emptyRow = {}
          this.dataFields.forEach(field => {
            emptyRow[field] = ''
          })
          this.tableData = [emptyRow]
        } else {
          this.tableData = []
        }
        
        this.barcodeLabels = []
        this.barcodePages = []
        this.currentLabelIndex = 0
        
        this.$message.success('数据已清空')
      })
    },
    
    // Excel上传处理
    handleExcelUpload(file) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          if (jsonData.length === 0) {
            this.$message.warning('Excel文件中没有数据')
            return
          }
          this._suppressGenerate = true
          this.importing = true
          this.showImportProgress = true
          this.importProgress = 0
          this.tableData = []
          this.dataFields = Object.keys(jsonData[0])
          this.setDefaultConfig()
          const total = jsonData.length
          const chunkSize = this.IMPORT_CHUNK_SIZE
          let imported = 0
          for (let i = 0; i < total; i += chunkSize) {
            const chunk = jsonData.slice(i, i + chunkSize)
            Array.prototype.push.apply(this.tableData, chunk)
            imported += chunk.length
            this.importProgress = Math.min(100, Math.round((imported / total) * 100))
            if ((i / chunkSize) % this.IMPORT_YIELD_INTERVAL === 0) await this.microYield()
          }
          this._suppressGenerate = false
          this.generateLabels()
          this.importProgress = 100
          setTimeout(()=>{ this.showImportProgress = false }, 600)
          this.$message.success(`成功增量导入 ${total} 条数据`)
        } catch (error) {
          console.error('Excel解析错误:', error)
          this.$message.error('Excel文件解析失败，请检查文件格式')
        } finally {
          this.importing = false
          this._suppressGenerate = false
        }
      }
      reader.readAsArrayBuffer(file.raw)
    },
    
    // 下载模板
    downloadTemplate() {
      const templateData = [
        { '产品名称': '示例产品1', '产品编码': '1234567890123', '规格': '规格A' },
        { '产品名称': '示例产品2', '产品编码': '2345678901234', '规格': '规格B' }
      ]
      const ws = XLSX.utils.json_to_sheet(templateData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, '条形码数据模板.xlsx')
    },
    
    // 更新表格数据
    updateTableData() {
      // 触发重新生成标签
      this.$nextTick(() => {
        this.generateLabels()
      })
    },
    
    // 布局变化处理
    onLayoutChange() {
      this.generateLabels()
    },
    
    // 页面类型切换处理
    onPageTypeChange() {
      this.generateLabels()
    },
    
    // ========== 标签生成方法 ==========
    
    // 生成所有标签数据
    generateLabels() {
      return this.generateLabelsAsync()
    },
    async generateLabelsAsync() {
      // 如果没有数据或没有选择条形码字段，清空标签
      if (!this.tableData.length || !this.config.barcodeField || !this.dataFields.includes(this.config.barcodeField)) {
        this.barcodeLabels = []
        this.barcodePages = []
        return
      }
      
      // 生成标签数据，过滤掉空行（条形码内容为空的行）
      const result = []
      const total = this.tableData.length
      for (let i = 0; i < total; i++) {
        const row = this.tableData[i]
        const rawVal = row[this.config.barcodeField]
        const safeStr = rawVal == null ? '' : String(rawVal)
        if (safeStr && safeStr.trim() !== '') {
          result.push({
            id: i,
            barcodeContent: safeStr,
            textContent: this.config.showText ? safeStr : '',
            rowData: row
          })
        }
        if (i % 800 === 0) await this.microYield()
      }
      this.barcodeLabels = result
      
      // 分页
      this.generatePages()
      
      // 生成条形码
      this.$nextTick(() => {
        this.generateBarcodes()
      })
    },
    
    // 分页处理
    generatePages() {
      const itemsPerPage = this.itemsPerPage
      this.barcodePages = []
      
      for (let i = 0; i < this.barcodeLabels.length; i += itemsPerPage) {
        this.barcodePages.push(this.barcodeLabels.slice(i, i + itemsPerPage))
      }
      
      // 确保当前标签索引有效
      if (this.currentLabelIndex >= this.barcodeLabels.length) {
        this.currentLabelIndex = Math.max(0, this.barcodeLabels.length - 1)
      }
    },
    
    // 生成条形码
    async generateBarcodes() {
      try {
        // 为当前预览标签生成条形码
        await this.generateCurrentLabelBarcode()
        
        // 仅在需要 ZIP 导出时生成所有隐藏条形码，减少 DOM / JsBarcode 开销
        if (this.showHiddenForZip) {
          await this.generateAllHiddenBarcodes()
        }
      } catch (error) {
        console.error('生成条形码失败:', error)
      }
    },
    
    // 为当前预览标签生成条形码
    async generateCurrentLabelBarcode() {
      if (!this.currentPreviewLabel || !this.currentPreviewLabel.barcodeContent) return
      
      await this.generateSingleBarcode('previewBarcode', this.currentPreviewLabel.barcodeContent)
    },
    
    // 为所有隐藏标签生成条形码
    async generateAllHiddenBarcodes() {
      for (let i = 0; i < this.barcodeLabels.length; i++) {
        const label = this.barcodeLabels[i]
        if (label.barcodeContent) {
          await this.generateSingleBarcode(`hiddenBarcode_${i}`, label.barcodeContent)
        }
      }
    },
    
    // 生成单个条形码
    async generateSingleBarcode(svgRef, content) {
      if (!content) return
      
      const svgArray = this.$refs[svgRef]
      if (!svgArray || svgArray.length === 0) return
      
      const svg = svgArray[0] || svgArray
      if (!svg) return
      
      try {
        // 清空SVG内容
        svg.innerHTML = ''
        
        // 生成条形码
        JsBarcode(svg, content, {
          format: this.config.barcodeType,
          width: 2,
          height: this.config.barcodeHeight * 2.834645669, // 转换为像素（1mm ≈ 2.83px）
          displayValue: this.config.showText, // 使用插件自带的文字显示
          background: 'transparent',
          lineColor: '#000000',
          margin: 2,
          fontSize: this.config.fontSize * 2.834645669, // 将mm转换为像素
          textAlign: 'center',
          textPosition: 'bottom',
          textMargin: this.config.textSpacing * 2.834645669, // 文字间距
          fontOptions: 'bold' // 字体选项
        })
      } catch (error) {
        console.error('生成条形码失败:', error)
        // 生成失败时显示错误文本
        svg.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="red" font-size="12">条形码生成失败</text>`
      }
    },
    
    // ========== 样式计算方法 ==========
    
    // 获取单个标签样式（用于预览）
    getSingleLabelStyle() {
      // 根据页面类型选择尺寸
      let width, height, padding
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        width = this.config.singlePageWidth + 'mm'
        height = this.config.singlePageHeight + 'mm'
        padding = '5mm'
      } else {
        width = '120mm' // A4多标签模式的固定预览尺寸
        height = '40mm'
        padding = '8mm'
      }
      
      return {
        width: width,
        height: height,
        border: this.config.showBorder ? `1px solid ${this.config.borderColor}` : '1px solid #ddd',
        backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'white',
        boxSizing: 'border-box',
        padding: padding,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }
    },
    
    // 获取隐藏标签样式（用于PDF生成）
    getHiddenLabelStyle() {
      // 根据页面类型选择尺寸
      let width, height, padding
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        width = this.config.singlePageWidth + 'mm'
        height = this.config.singlePageHeight + 'mm'
        padding = '5mm'
      } else {
        width = '100mm' // A4多标签模式的固定尺寸
        height = '35mm'
        padding = '5mm'
      }
      
      return {
        width: width,
        height: height,
        border: this.config.showBorder ? `1px solid ${this.config.borderColor}` : 'none',
        backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'transparent',
        boxSizing: 'border-box',
        padding: padding,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontFamily: 'Arial, "Microsoft YaHei", "SimSun", sans-serif'
      }
    },
    
    // 获取标签类名（简化版本，因为文字位置由插件控制）
    getLabelClass() {
      return {
        'barcode-label': true
      }
    },
    
    // 获取内容样式
    getContentStyle() {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
        // 移除 gap，因为文字现在是条形码的一部分
      }
    },
    
    // 获取文字样式
    // ========== 预览控制方法 ==========
    
    // 上一个标签
    prevLabel() {
      if (this.currentLabelIndex > 0) {
        this.currentLabelIndex--
        this.$nextTick(() => {
          this.generateCurrentLabelBarcode()
        })
      }
    },
    
    // 下一个标签
    nextLabel() {
      if (this.currentLabelIndex < this.barcodeLabels.length - 1) {
        this.currentLabelIndex++
        this.$nextTick(() => {
          this.generateCurrentLabelBarcode()
        })
      }
    },
    
    // ========== PDF生成方法 ==========
    
    // 生成PDF
    async generatePDF() {
      if (!this.barcodeLabels.length) {
        this.$message.warning('请先添加数据')
        return
      }
      
      try {
        const totalLabels = this.barcodeLabels.length
        // 步骤1: 准备字体 (0-15%)，增加全局缓存避免重复 fetch / 解析
        this.generateProgress = 5
        const pdfDoc = await PDFDocument.create()
        pdfDoc.registerFontkit(fontkit)
        if (!window.__sharedFontBytes) {
          window.__sharedFontBytes = await fetch(sourceHanFontUrl).then(r => r.arrayBuffer())
        }
        const customFont = await pdfDoc.embedFont(window.__sharedFontBytes, { subset: true })
        this.generateProgress = 15

        // 步骤2: 条形码图像缓存 (15-35%)，分批让出主线程
        const barcodeCache = []
        const dedupeMap = new Map()
        for (let i = 0; i < totalLabels; i++) {
          const label = this.barcodeLabels[i]
          try {
            let image = dedupeMap.get(label.barcodeContent)
            if (!image) {
              const dataUrl = await this.getBarcodePngDataUrl(label.barcodeContent)
              const pngBytes = this.dataURLToUint8Array(dataUrl)
              image = await pdfDoc.embedPng(pngBytes)
              dedupeMap.set(label.barcodeContent, image)
            }
            barcodeCache.push({ index: i, image, label })
          } catch (e) {
            console.warn('条形码缓存失败 index=', i, e)
          }
          if (i % this.YIELD_ITEM_INTERVAL === 0) await this.microYield()
          this.generateProgress = 15 + Math.floor((i / Math.max(1,totalLabels)) * 20)
        }

        // 步骤3: 分页绘制 (35-90%)，分页间让步
        const itemsPerPage = this.itemsPerPage
        const totalPages = Math.ceil(totalLabels / itemsPerPage)
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          this.drawBarcodePDFPage(pdfDoc, pageIndex, barcodeCache, customFont)
          if (pageIndex % this.YIELD_PAGE_INTERVAL === 0) await this.microYield()
          this.generateProgress = 35 + Math.floor(((pageIndex + 1) / totalPages) * 55)
        }

        // 步骤4: 保存 (90-100%)
        this.generateProgress = 95
        const pdfBytes = await pdfDoc.save()
        this.generateProgress = 100
        this.downloadPDF(pdfBytes)
        setTimeout(() => { this.showProgress = false }, 800)
        this.$message.success(`PDF生成成功，包含 ${totalLabels} 个标签`)
      } catch (error) {
        console.error('PDF生成失败:', error)
        this.$message.error('PDF生成失败：' + error.message)
        this.showProgress = false
      } finally {
        this.generating = false
      }
    },
    
    // === 新增: 生成条形码 PNG DataURL ===
    async getBarcodePngDataUrl(content) {
      // 创建临时 SVG，通过 JsBarcode 生成，再转 canvas 然后导出 PNG
      return new Promise((resolve, reject) => {
        try {
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
          JsBarcode(svg, content, {
            format: this.config.barcodeType,
            width: 2,
            height: this.config.barcodeHeight * 3.7795, // 1mm≈3.78px (设备相关，这里足够清晰)
            displayValue: this.config.showText,
            background: 'transparent',
            lineColor: '#000000',
            margin: 2,
            fontSize: this.config.fontSize * 3.7795,
            textAlign: 'center',
            textPosition: 'bottom',
            textMargin: this.config.textSpacing * 3.7795,
            fontOptions: 'bold'
          })
          const xml = new XMLSerializer().serializeToString(svg)
          const svg64 = window.btoa(unescape(encodeURIComponent(xml)))
          const image64 = 'data:image/svg+xml;base64,' + svg64
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
          }
          img.onerror = reject
          img.src = image64
        } catch (e) { reject(e) }
      })
    },

    // mm 转 pt
    mmToPt(mm) { return mm * 2.834645669 },

    hexToPdfRgb(hex) {
      if (!hex) return rgb(0,0,0)
      const h = hex.replace('#','')
      if (h.length === 3) {
        const r = parseInt(h[0]+h[0],16)/255
        const g = parseInt(h[1]+h[1],16)/255
        const b = parseInt(h[2]+h[2],16)/255
        return rgb(r,g,b)
      }
      if (h.length === 6) {
        const r = parseInt(h.substring(0,2),16)/255
        const g = parseInt(h.substring(2,4),16)/255
        const b = parseInt(h.substring(4,6),16)/255
        return rgb(r,g,b)
      }
      return rgb(0,0,0)
    },

    drawBarcodePDFPage(pdfDoc, pageIndex, cache, font) {
      let pageWidthPt, pageHeightPt
      let page
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        pageWidthPt = this.mmToPt(this.config.singlePageWidth)
        pageHeightPt = this.mmToPt(this.config.singlePageHeight)
        page = pdfDoc.addPage([pageWidthPt, pageHeightPt])
      } else {
        pageWidthPt = 595.28; pageHeightPt = 841.89
        page = pdfDoc.addPage([pageWidthPt, pageHeightPt])
      }

      const itemsPerPage = this.itemsPerPage
      const start = pageIndex * itemsPerPage
      const end = Math.min(start + itemsPerPage, this.barcodeLabels.length)

      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        if (start < end) {
          this.drawSingleBarcodeLabel(page, cache[start], 0, 0, pageWidthPt, pageHeightPt, font)
        }
      } else {
        const margin = 20
        const availableWidth = pageWidthPt - margin * 2
        const availableHeight = pageHeightPt - margin * 2
        const itemWidth = availableWidth / this.config.cols
        const itemHeight = availableHeight / this.config.rows
        for (let i = start; i < end; i++) {
          const idxInPage = i - start
          const row = Math.floor(idxInPage / this.config.cols)
          const col = idxInPage % this.config.cols
          const x = margin + col * itemWidth
          const y = pageHeightPt - margin - (row + 1) * itemHeight
          this.drawSingleBarcodeLabel(page, cache[i], x, y, itemWidth, itemHeight, font)
        }
      }
    },

    drawSingleBarcodeLabel(page, cacheItem, x, y, w, h, font) {
      if (!cacheItem) return
      const { image, label } = cacheItem
      const barcodeWidthPt = this.mmToPt(this.config.barcodeWidth)
      const barcodeHeightPt = this.mmToPt(this.config.barcodeHeight)
      const hasText = this.config.showText
      const fontSizePt = this.mmToPt(this.config.fontSize)
      const textSpacingPt = this.mmToPt(this.config.textSpacing)
      const text = hasText ? label.barcodeContent : ''

      // 背景边框
      if (this.config.showBackground) {
        page.drawRectangle({ x, y, width: w, height: h, color: this.hexToPdfRgb(this.config.backgroundColor) })
      }
      if (this.config.showBorder) {
        page.drawRectangle({ x, y, width: w, height: h, borderWidth: 1, borderColor: this.hexToPdfRgb(this.config.borderColor) })
      }

      // 条形码放置（上方或居中）
      let totalHeight = barcodeHeightPt + (hasText ? (textSpacingPt + fontSizePt) : 0)
      const startY = y + (h - totalHeight) / 2
      const barcodeX = x + (w - barcodeWidthPt) / 2
      const barcodeY = startY + (hasText ? (fontSizePt + textSpacingPt) : 0)
      page.drawImage(image, { x: barcodeX, y: barcodeY, width: barcodeWidthPt, height: barcodeHeightPt })

      // 文字（条形码内容）
      if (hasText) {
        const textWidth = font.widthOfTextAtSize(text, fontSizePt)
        const tx = x + (w - textWidth) / 2
        const ty = barcodeY + barcodeHeightPt + textSpacingPt
        page.drawText(text, { x: tx, y: ty, size: fontSizePt, font, color: rgb(0,0,0) })
      }
    },
    
    // DataURL转Uint8Array
    dataURLToUint8Array(dataURL) {
      const base64String = dataURL.split(',')[1]
      const binaryString = atob(base64String)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes
    },
    
    // 下载PDF
    downloadPDF(pdfBytes) {
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      // 根据页面类型生成不同的文件名
  const modeText = (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') ? '单页标签' : 'A4多标签'
      const timestamp = new Date().getTime()
      a.download = `${modeText}_批量条形码_${timestamp}.pdf`
      
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // ========== 图片ZIP生成方法 ==========
    
    // 生成单张图片并打包到ZIP
    async generateZipImages() {
      if (!this.barcodeLabels.length) {
        this.$message.warning('请先添加数据')
        return
      }
      
      this.generatingZip = true
      this.showZipProgress = true
      this.zipProgress = 0
  this.showHiddenForZip = true
  await this.$nextTick()
      
      try {
        // 步骤1: 渲染条形码 (0-20%)
        this.zipProgress = 10
        await this.generateAllHiddenBarcodes()
        
        // 步骤2: 创建ZIP实例 (20-30%)
        this.zipProgress = 20
        const zip = new JSZip()
        const folder = zip.folder('条形码图片')
        
        // 步骤3: 批量生成图片并添加到ZIP (30-90%)
        const totalLabels = this.barcodeLabels.length
        for (let i = 0; i < totalLabels; i++) {
          const label = this.barcodeLabels[i]
          const hiddenElement = this.$refs[`hiddenLabel_${i}`]
          
          if (hiddenElement && (hiddenElement[0] || hiddenElement)) {
            const element = hiddenElement[0] || hiddenElement
            
            try {
              // 使用html2canvas将DOM转换为图片
              const canvas = await html2canvas(element, {
                backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'transparent',
                scale: 3, // 高分辨率
                useCORS: true,
                allowTaint: true,
                width: element.offsetWidth,
                height: element.offsetHeight,
                logging: false,
                removeContainer: true
              })
              
              // 将Canvas转换为Blob
              const blob = await new Promise(resolve => {
                canvas.toBlob(resolve, 'image/png', 1.0)
              })
              
              // 生成文件名
              const fileName = this.generateImageFileName(label, i)
              
              // 添加到ZIP
              folder.file(fileName, blob)
              
            } catch (error) {
              console.error(`生成标签 ${i + 1} 图片失败:`, error)
            }
          }
          
          // 更新进度 (30-90%)
          const progress = 30 + Math.round(((i + 1) / totalLabels) * 60)
          this.zipProgress = progress
          
          // 给UI更新的时间
          await this.$nextTick()
        }
        
        // 步骤4: 生成ZIP文件并下载 (90-100%)
        this.zipProgress = 95
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const timestamp = new Date().getTime()
        const zipFileName = `批量条形码_${timestamp}.zip`
        
        this.zipProgress = 100
        saveAs(zipBlob, zipFileName)
        
        // 延迟隐藏进度条
        setTimeout(() => {
          this.showZipProgress = false
        }, 1500)
        
        this.$message.success(`成功生成包含 ${this.barcodeLabels.length} 张图片的ZIP文件`)
        
      } catch (error) {
        console.error('ZIP生成失败:', error)
        this.$message.error('ZIP生成失败：' + error.message)
        this.showZipProgress = false
      } finally {
        this.generatingZip = false
        this.showHiddenForZip = false
      }
    },
    
    // 生成图片文件名
    generateImageFileName(label, index) {
      // 使用条形码内容作为文件名，但要处理特殊字符
      let fileName = label.barcodeContent || `条形码_${index + 1}`
      
      // 替换文件名中的特殊字符
      fileName = fileName.replace(/[<>:"/\\|?*]/g, '_')
      
      // 限制文件名长度
      if (fileName.length > 100) {
        fileName = fileName.substring(0, 100)
      }
      
      return `${fileName}.png`
    }
  }
}
</script>

<style scoped>
.batch-barcode-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.page-header h1 {
  margin: 10px 0;
  font-size: 28px;
  font-weight: bold;
}

.page-header p {
  margin: 0;
  opacity: 0.9;
}

.main-config-section {
  margin-bottom: 20px;
}

.clearfix {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.section-title {
  font-weight: bold;
  color: #303133;
  font-size: 16px;
}

.data-actions {
  display: flex;
  gap: 8px;
}

.config-group {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #fafbfc;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.group-title {
  margin: 0 0 15px 0;
  color: #606266;
  font-size: 14px;
  font-weight: bold;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.preview-container {
  position: relative;
  background: #f8f9fa;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 30px;
  margin: 0 auto;
  max-width: 800px;
  min-height: 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.single-label-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.label-info {
  text-align: center;
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  max-width: 400px;
}

.label-info p {
  margin: 8px 0;
  color: #606266;
}

.label-info strong {
  color: #303133;
}

.barcode-label-item {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.barcode-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.barcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.page-info {
  position: absolute;
  bottom: 5px;
  right: 10px;
  font-size: 12px;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .batch-barcode-container {
    padding: 10px;
  }

  .page-header {
    padding: 20px 10px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .preview-container {
    width: 100%;
    padding: 10mm;
  }
}
</style>
